import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { EChartsOption } from 'echarts';

interface ChartWidgetWithTotalProps {
    title: string;
    total: string | number;
    chartOption: EChartsOption;
    chartHeight?: string;
    onTotalClick?: () => void;
    onChartClick?: (params: any) => void;
    isSmallScreen?: boolean;
    isMediumScreen?: boolean;
    showDivider?: boolean;
}

const ChartWidgetWithTotal: React.FC<ChartWidgetWithTotalProps> = ({
    title,
    total,
    chartOption,
    chartHeight = '380px',
    onTotalClick,
    onChartClick,
    isSmallScreen = false,
}) => {
    // Ensure pie series is centered horizontally at 50% and legend is aligned to the left
    const centeredChartOption: EChartsOption = {
        ...chartOption,
        tooltip: {
            confine: true,
            appendToBody: false,
            backgroundColor: "#ffffff",
            trigger: "item",
            textStyle: { color: "#000000" }
        },
        legend: Array.isArray(chartOption.legend)
            ? chartOption.legend.map((leg) => ({
                ...leg,
                left: 'left',
            }))
            : chartOption.legend
                ? {
                    ...chartOption.legend,
                    left: 'left',
                }
                : chartOption.legend,
        series: Array.isArray(chartOption.series)
            ? chartOption.series.map((series) => {
                if (
                    typeof series === 'object' &&
                    series !== null &&
                    'type' in series &&
                    series.type === 'pie'
                ) {
                    return {
                        ...series,
                        center: ['50%', '42%'],
                    };
                }

                return series;
            })
            : chartOption.series,
    };

    return (
        <Stack
            bgcolor="#FFFFFF"
            fontFamily="Montserrat"
            borderRadius="5px"
            p={2}
            sx={{
                width: '100%',
                height: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >

            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    mb: 1,
                }}
            >
                <Typography
                    variant="widgetTitle"
                    sx={{
                        fontSize: { xs: '14px', lg: '16px' },
                        fontWeight: 500,
                        color: '#000000',
                        textAlign: 'left',
                        lineHeight: 1.2,
                        width: '100%',
                        wordBreak: 'break-word',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {title}
                </Typography>

                <Typography
                    variant={isSmallScreen ? 'h4' : 'h3'}
                    sx={{
                        fontWeight: '300',
                        textAlign: 'left',
                        color: '#000000',
                        mt: 0.5,
                        cursor: onTotalClick ? 'pointer' : 'default',
                        transition: 'all 0.3s ease',
                        '&:hover': onTotalClick
                            ? {
                                color: '#21ddebff',
                                transform: 'scale(1.05)',
                            }
                            : {},
                    }}
                    onClick={onTotalClick}
                >
                    {total}
                </Typography>
            </Box>

            {/* Pie chart centered */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                <ReactECharts
                    option={centeredChartOption}
                    notMerge={true}
                    style={{
                        width: '100%',
                        height: '100%',
                    }}
                    opts={{
                        renderer: 'canvas',
                    }}
                    onEvents={
                        onChartClick
                            ? { click: onChartClick }
                            : undefined
                    }
                />
            </Box>
        </Stack>
    );
};

export default ChartWidgetWithTotal;
